import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import { fetchStorageRequests, approveStorageRequest, denyStorageRequest } from '../services/api';
import type { StorageRequest } from '../types';
import Button from '../components/Button';
import Chip from '../components/Chip';
import Avatar from '../components/Avatar';
import Modal from '../components/Modal';
import Icon from '../components/Icon';
import { useAppStore } from '../store/useAppStore';

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

const EXTRA_GB_PRICE = 0.04;

export default function StorageRequests() {
  const { toast } = useAppStore();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showPayment, setShowPayment] = useState(false);
  const [paymentStep, setPaymentStep] = useState<'confirm' | 'processing' | 'done'>('confirm');

  const { data: pending = [] } = useQuery({
    queryKey: [...qk.storageRequests(), 'pending'],
    queryFn: () => fetchStorageRequests('pending'),
  });

  const { data: resolved = [] } = useQuery({
    queryKey: [...qk.storageRequests(), 'resolved'],
    queryFn: () => fetchStorageRequests('approved'),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => approveStorageRequest(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.storageRequests() }); toast('Approved'); },
  });

  const denyMutation = useMutation({
    mutationFn: (id: string) => denyStorageRequest(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.storageRequests() }); toast('Denied'); },
  });

  const selArr = [...selected];
  const selRequests = pending.filter(r => selected.has(r.id));
  const totalGB = selRequests.reduce((s, r) => s + r.amount_bytes / (1024 * 1024 * 1024), 0);
  const totalCost = totalGB * EXTRA_GB_PRICE;

  function handleBulkApprove() {
    setShowPayment(true);
    setPaymentStep('confirm');
  }

  function handlePayment() {
    setPaymentStep('processing');
    setTimeout(() => {
      setPaymentStep('done');
      setTimeout(() => {
        selArr.forEach(id => approveMutation.mutate(id));
        setSelected(new Set());
        setShowPayment(false);
        setPaymentStep('confirm');
      }, 1200);
    }, 2000);
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--line)', background: 'var(--card)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 700 }}>Storage requests</h2>
          {pending.length > 0 && (
            <span style={{ background: 'var(--accent)', color: 'white', fontSize: 11, fontWeight: 700, borderRadius: 'var(--r-full)', padding: '2px 7px' }}>
              {pending.length}
            </span>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {/* Pending */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 10 }}>Pending approval</div>

          {pending.length === 0 ? (
            <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>
              <Icon name="check" size={28} color="var(--success)" />
              <div style={{ marginTop: 8, fontWeight: 600, color: 'var(--ink-2)' }}>All caught up</div>
              <div style={{ fontSize: 'var(--fs-sm)', marginTop: 4 }}>No pending storage requests.</div>
            </div>
          ) : (
            <>
              {/* Bulk action bar */}
              {selArr.length > 0 && (
                <div style={{ background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 8, padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{selArr.length} selected</span>
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
                    {formatBytes(selRequests.reduce((s, r) => s + r.amount_bytes, 0))} · ${totalCost.toFixed(2)}/mo
                  </span>
                  <div style={{ flex: 1 }} />
                  <Button kind="danger" size="sm" onClick={() => { selArr.forEach(id => denyMutation.mutate(id)); setSelected(new Set()); }}>
                    Deny {selArr.length}
                  </Button>
                  <Button kind="accent" size="sm" onClick={handleBulkApprove}>
                    Approve {selArr.length} & bill
                  </Button>
                </div>
              )}

              {/* Select all */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingLeft: 2 }}>
                <label className="t-check" style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
                  <input
                    type="checkbox"
                    checked={selArr.length === pending.length && pending.length > 0}
                    onChange={e => setSelected(e.target.checked ? new Set(pending.map(r => r.id)) : new Set())}
                  />
                  <span>Select all</span>
                </label>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pending.map(r => (
                  <RequestRow
                    key={r.id}
                    request={r}
                    selected={selected.has(r.id)}
                    onSelect={v => setSelected(prev => { const s = new Set(prev); v ? s.add(r.id) : s.delete(r.id); return s; })}
                    onApprove={() => approveMutation.mutate(r.id)}
                    onDeny={() => denyMutation.mutate(r.id)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Resolved */}
        {resolved.length > 0 && (
          <>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 10 }}>History</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {resolved.slice(0, 10).map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 8, fontSize: 'var(--fs-sm)' }}>
                  <Avatar name={r.member?.name ?? r.user_id} size="xs" />
                  <span style={{ flex: 1, color: 'var(--ink-2)' }}>{r.member?.name ?? r.user_id.slice(0, 10)} requested {formatBytes(r.amount_bytes)}</span>
                  <Chip tone={r.status === 'approved' ? 'success' : 'danger'} dot>{r.status}</Chip>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Payment modal */}
      <Modal
        open={showPayment}
        onClose={() => paymentStep !== 'processing' && setShowPayment(false)}
        title="Approve & bill"
        closeOnBackdrop={paymentStep !== 'processing'}
        footer={
          paymentStep === 'confirm' ? (
            <>
              <Button kind="ghost" onClick={() => setShowPayment(false)}>Cancel</Button>
              <Button kind="accent" onClick={handlePayment}>Continue to payment</Button>
            </>
          ) : paymentStep === 'done' ? (
            <Button kind="accent" onClick={() => setShowPayment(false)}>Done</Button>
          ) : undefined
        }
      >
        {paymentStep === 'confirm' && (
          <div className="t-stack">
            {selRequests.map(r => (
              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--line)', fontSize: 'var(--fs-sm)' }}>
                <span>{r.member?.name ?? r.user_id.slice(0, 10)}</span>
                <span style={{ fontFamily: 'var(--font-mono)' }}>+{formatBytes(r.amount_bytes)} · ${(r.amount_bytes / (1024 * 1024 * 1024) * EXTRA_GB_PRICE).toFixed(2)}/mo</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontWeight: 700, fontSize: 'var(--fs-md)' }}>
              <span>Total</span>
              <span style={{ fontFamily: 'var(--font-mono)' }}>${totalCost.toFixed(2)}/mo</span>
            </div>
            <div className="t-alert t-alert--info"><Icon name="info" size={13} />Payment is processed by the Softknack platform.</div>
          </div>
        )}
        {paymentStep === 'processing' && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <span className="t-spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
            <div style={{ marginTop: 16, fontWeight: 600 }}>Transferring to the Softknack platform…</div>
          </div>
        )}
        {paymentStep === 'done' && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Icon name="check" size={40} color="var(--success)" />
            <div style={{ marginTop: 12, fontWeight: 700, fontSize: 'var(--fs-lg)' }}>Payment confirmed</div>
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', marginTop: 6 }}>Storage quotas have been updated.</div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function RequestRow({ request: r, selected, onSelect, onApprove, onDeny }: {
  request: StorageRequest;
  selected: boolean;
  onSelect: (v: boolean) => void;
  onApprove: () => void;
  onDeny: () => void;
}) {
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <input type="checkbox" checked={selected} onChange={e => onSelect(e.target.checked)} style={{ accentColor: 'var(--accent)', flexShrink: 0 }} />
      <Avatar name={r.member?.name ?? r.user_id} size="sm" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: 'var(--fs-sm)' }}>{r.member?.name ?? r.user_id.slice(0, 12)}</div>
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
          Requesting +{formatBytes(r.amount_bytes)} · {r.reason}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <Button kind="outline" size="sm" onClick={onDeny}>Deny</Button>
        <Button kind="accent" size="sm" onClick={onApprove}>Approve</Button>
      </div>
    </div>
  );
}
